import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Reveal, ChapterHead } from "./Reveal";

const THEMES = [
  "TECHNOLOGY", "SOCIETY", "IDENTITY", "BLACKNESS", "TIME", "AMERICA",
  "ATTENTION", "CREATIVITY", "GENERATION", "HUMANITY", "THE FUTURE",
];

const STUDIES = [
  { src: "/images/jeremiah-mind-map.jpeg", cap: "YOUR MIND IS THE MAP", tag: "VIDEO", video: "/videos/your-mind-is-the-map.mp4" },
  { src: "/images/jeremiah-start-aware.jpeg", cap: "START BEING AWARE", tag: "VIDEO", video: "/videos/start-being-aware.mp4" },
];

const VideoModal = ({ video, onClose }) => {
  const [error, setError] = useState(false);
  const ytMatch = video.youtube?.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  const ytId = ytMatch?.[1];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#05070F]/95 backdrop-blur-md px-4"
      onClick={onClose}
      data-testid="video-modal"
    >
      <button
        onClick={onClose}
        data-testid="video-modal-close"
        className="absolute top-6 right-6 font-meta text-xs tracking-[0.25em] text-blue-100/60 hover:text-white border border-blue-200/30 rounded-full px-5 py-2.5 transition-colors duration-300"
      >
        CLOSE
      </button>
      <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        {ytId ? (
          <div className="aspect-video w-full border border-blue-100/15">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&rel=0`}
              title={video.cap}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="video-player"
            />
          </div>
        ) : !error ? (
          <video
            src={video.video}
            poster={video.src}
            controls
            autoPlay
            playsInline
            onError={() => setError(true)}
            className="w-full border border-blue-100/15"
            data-testid="video-player"
          />
        ) : (
          <div className="border border-blue-100/15 p-10 sm:p-16 text-center" data-testid="video-fallback">
            <p className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight">{video.cap}</p>
            <p className="text-sm font-light text-blue-100/60 mt-4">
              This clip is being moved over. Watch it on Jeremiah’s channels right now:
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-6">
              <a
                href="https://www.instagram.com/j.erem1ah/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="video-fallback-instagram"
                className="font-meta text-xs tracking-[0.25em] border border-blue-200/30 rounded-full px-6 py-3 hover:bg-[#4F7CFF] hover:border-[#4F7CFF] hover:text-white transition-colors duration-300"
              >
                INSTAGRAM ↗
              </a>
              <a
                href="https://youtube.com/@j.3r3m1ah"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="video-fallback-youtube"
                className="font-meta text-xs tracking-[0.25em] border border-blue-200/30 rounded-full px-6 py-3 hover:bg-[#4F7CFF] hover:border-[#4F7CFF] hover:text-white transition-colors duration-300"
              >
                YOUTUBE ↗
              </a>
            </div>
          </div>
        )}
        <p className="font-meta text-[10px] tracking-[0.25em] text-blue-300/50 mt-4">{video.cap} — VIDEO</p>
      </div>
    </motion.div>
  );
};

export const Observations = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <section id="observations" className="px-6 sm:px-12 lg:px-20 py-24 sm:py-32 max-w-7xl mx-auto" data-testid="observations-section">
      <ChapterHead index="CHAPTER 07" title="OBSERVATIONS" />

      <Reveal delay={0.05}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mt-16 items-start" data-testid="observations-studies">
          {STUDIES.map((s) => (
            <figure key={s.cap} className="bg-[#0A0A0A] group overflow-hidden" data-testid={`study-${s.cap.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="overflow-hidden relative">
                <img
                  src={s.src}
                  alt={s.cap}
                  className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-700"
                />
                <span className="absolute top-3 right-3 font-meta text-[9px] tracking-[0.25em] bg-black/70 backdrop-blur-sm border border-white/15 px-2.5 py-1 text-neutral-300">
                  {s.tag}
                </span>
                {s.video && (
                  <button
                    onClick={() => setActiveVideo(s)}
                    data-testid={`play-${s.cap.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    aria-label={`Play ${s.cap}`}
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                  >
                    <span className="w-16 h-16 rounded-full border border-white/40 bg-black/50 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#4F7CFF] group-hover:border-[#4F7CFF] group-hover:scale-110 transition-all duration-300">
                      <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                    </span>
                  </button>
                )}
              </div>
              <figcaption className="font-meta text-[10px] tracking-[0.25em] text-neutral-500 px-4 py-3 border-t border-white/10 group-hover:text-white transition-colors duration-300">
                {s.cap}
              </figcaption>
            </figure>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <p className="font-meta text-xs tracking-[0.3em] text-neutral-500 mt-16 mb-6">THEMES UNDER WATCH</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {THEMES.map((t, i) => (
            <div
              key={t}
              className="bg-[#0A0A0A] p-5 sm:p-8 hover:bg-white hover:text-black transition-colors duration-300 group"
              data-testid={`theme-${t.toLowerCase().replace(/ /g, "-")}`}
            >
              <span className="font-meta text-[10px] tracking-[0.25em] text-neutral-500 group-hover:text-black/40 transition-colors duration-300">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="font-display text-sm sm:text-xl font-bold uppercase tracking-tight leading-tight mt-3 break-words">{t}</p>
            </div>
          ))}
          <div className="bg-white text-black p-6 sm:p-8 flex flex-col justify-between gap-6" data-testid="observations-socials-cell">
            <span className="font-meta text-[10px] tracking-[0.25em] opacity-50">FOLLOW THE WORK</span>
            <div className="flex flex-col gap-3">
              <a
                href="https://www.instagram.com/j.erem1ah/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="observations-instagram-link"
                className="font-display text-lg sm:text-xl font-bold uppercase tracking-tight hover:text-[#4F7CFF] transition-colors duration-300"
              >
                INSTAGRAM ↗ <span className="text-sm font-medium normal-case tracking-normal">@j.erem1ah</span>
              </a>
              <a
                href="https://youtube.com/@j.3r3m1ah"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="observations-youtube-link"
                className="font-display text-lg sm:text-xl font-bold uppercase tracking-tight hover:text-[#4F7CFF] transition-colors duration-300"
              >
                YOUTUBE ↗ <span className="text-sm font-medium normal-case tracking-normal">@j.3r3m1ah</span>
              </a>
            </div>
          </div>
        </div>
      </Reveal>

      {activeVideo && <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />}
    </section>
  );
};
